import { useEffect, useState } from 'react';
import { loggingAPI } from '../../services/api';

// ═══════════════════════════════════════════════
// 测井 — 共享类型 + 带进程缓存的加载 hook
// ═══════════════════════════════════════════════
export type LogPoint = { depth: number; value: number };
export type LogCurve = {
  id: number;
  mnemonic: string;
  unit: string;
  description: string;
  min_value: number | null;
  max_value: number | null;
  data_points: LogPoint[];
  point_count?: number;
};
export type LogInterp = {
  id: number;
  top_depth: number;
  bottom_depth: number;
  lithology: string;
  porosity: number | null;
  permeability: number | null;
  water_saturation: number | null;
  oil_saturation: number | null;
  fluid_type: string;
  notes: string;
};

const CURVE_CACHE: Record<number, LogCurve[]> = {};
const INTERP_CACHE: Record<number, LogInterp[]> = {};

const un = (d: any) => (Array.isArray(d) ? d : d?.results ?? []);

export function useLogging(projectId?: number) {
  const [curves, setCurves] = useState<LogCurve[]>(projectId ? CURVE_CACHE[projectId] ?? [] : []);
  const [interps, setInterps] = useState<LogInterp[]>(projectId ? INTERP_CACHE[projectId] ?? [] : []);
  const [loading, setLoading] = useState(!(projectId && CURVE_CACHE[projectId]));

  // 缓存只用来"先画出来别闪白", **不再用它跳过请求**。
  //
  // 原来是 `if (CURVE_CACHE[pid]) { 用缓存; return; }` —— 两个毛病:
  //  1. 空数组在 JS 里是 truthy。用户在数据还没导进来时看过一眼这页, `[]` 就被
  //     缓存住了, 之后再导入多少曲线, 不刷新整页就永远显示"0 条曲线"。
  //  2. 就算缓存非空, 数据中心新应用的曲线也进不来 —— 缓存从不失效。
  // 现在改成 stale-while-revalidate: 有缓存先拿来画(不转圈), 同时照常拉一次覆盖它。
  useEffect(() => {
    if (!projectId) { setCurves([]); setInterps([]); setLoading(false); return; }
    const cached = CURVE_CACHE[projectId];
    if (cached) { setCurves(cached); setInterps(INTERP_CACHE[projectId] ?? []); }
    setLoading(!cached);

    let alive = true;
    Promise.all([loggingAPI.getCurves(projectId), loggingAPI.getInterpretation(projectId)])
      .then(([c, i]) => {
        const cs: LogCurve[] = un(c.data);
        const is: LogInterp[] = un(i.data).sort((a: LogInterp, b: LogInterp) => a.top_depth - b.top_depth);
        CURVE_CACHE[projectId] = cs; INTERP_CACHE[projectId] = is;
        if (!alive) return;   // 请求回来时用户已经切走了, 别把旧项目的数据写进去
        setCurves(cs); setInterps(is);
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [projectId]);

  return { curves, interps, loading };
}

// 常见曲线配色
export const CURVE_COLOR: Record<string, string> = {
  GR: '#2E7D32', SP: '#00838F', CAL: '#6D7882', CALI: '#6D7882', BS: '#8A929A',
  RT: '#C62828', RD: '#B71C1C', ILD: '#D84315', RI: '#AD1457', IM: '#C2185B',
  RS: '#EF6C00', ILS: '#F57C00', MSFL: '#FB8C00', RXO: '#8D3B11', RW: '#795548',
  DEN: '#5D4037', CNL: '#283593', AC: '#6A1B9A', DTS: '#00ACC1', PE: '#00796B',
  POR: '#1565C0', SW: '#1976D2', VSH: '#689F38',
  DEV: '#546E7A', AZI: '#455A64', NMR: '#512DA8', T2: '#7B1FA2',
  FMI: '#3949AB', UBI: '#1976D2', VP: '#0277BD', VS: '#0097A7',
  DTN: '#5E35B1', TOC: '#D4770C', BI: '#AF6C00', PERM: '#2F6F4E',
  YM: '#7B4F2A', PR: '#6A4C93', SHMIN: '#8E5572', BIOT: '#4F6680',
};
export const curveColor = (m: string) => CURVE_COLOR[m?.toUpperCase()] || '#4F6680';

export const LTH = {
  bg: '#FFFFFF', canvasBg: '#FFFFFF', grid: 'rgba(30,42,58,0.07)',
  text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  accent: '#C2533A', headerBorder: '#D6DAE0', panelHeaderBg: '#F4F6F9',
  fontUI: '"Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif',
  fontMono: '"Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace',
};
